/** @jsx h */
import { h } from 'preact'

import BlockchainProof from './BlockchainProofSection'
import EvidencedMeaning from './EvidencedMeaning'
import Footer from './Footer'
import EvidenceSection from './EvidenceSection'
import HowAchievedSection from './HowAchievedSection'
import JourneyClose from '../journeys/Close'
import { ProofPointStickyHeader } from './StickyHeader'
import ProductSummary from './ProductSummary'
import Feedback from '../feedback/Feedback'
import ThirdPartyVerification from './ThirdPartyVerification'
import ClaimDescription from './ClaimDescription'
import Lookup from './recycling/Lookup'
import ThemeSection from './ThemeSection'
import ThemeSectionV1 from './ThemeSectionV1'
import DescriptionSection from './DescriptionSection'
import QuoteSection from './QuoteSection'
import VerificationSection from './VerificationSection'
import EvidenceVerifierSection from './EvidenceVerifierSection'
import EvidenceFromActivitySection from './EvidenceFromActivitySection'
import t from '../../../utils/translate'

const verifierSection = (name, claimStatus, verifier, activities, eventLabel) => {
  if (verifier == null && activities == null) return null

  const claimName = name
  const { name: verifierName, avatarUrl, description, href } =
    verifier || activities[0].agent.name

  return (
    <EvidenceVerifierSection
      avatarUrl={avatarUrl}
      claimName={claimName}
      claimStatus={claimStatus}
      description={description}
      eventLabel={eventLabel}
      href={href}
      verifierName={verifierName}
    />
  )
}

const withEvidence = (evidence, issuer, eventLabel) => {
  const { evidenceLabel, description, url, attachment, document } = evidence
  const evidenceUrl = (attachment && attachment.mediaUrl) || (document && document.documentUrl) || url
  if (!evidenceUrl) return null

  return (
    <EvidenceSection
      label={evidenceLabel}
      description={description}
      url={evidenceUrl}
      eventLabel={eventLabel}
      issuer={issuer}
    />
  )
}

const withEvidenceFromActivity = activity => {
  const { agent, startDate } = activity
  const options = { year: 'numeric', month: 'long', day: 'numeric' }
  const dateString = new Intl.DateTimeFormat('en-GB', options).format(
    new Date(startDate)
  )

  return (
    <EvidenceFromActivitySection
      {...agent.services[0]}
      title={t('proof_points.js.evidence.see_proof')}
      dateStatement={`Data provided: ${dateString}.`}
    />
  )
}

const EvidenceCard = ({
  analyticsName,
  evidence,
  activities,
  name,
  initiatives,
  issuer,
  statement,
  verifier,
  subject,
  inJourney,
  networkExplorerUrl,
  ipfsHash,
  iconSvg,
  description,
  shortDescription,
  scheme,
  typeLabel,
  hideAssociatedProductInModal,
  recyclingLookupUrl,
  theme,
  journeys,
  onStoryClick,
  onAboutClick,
  showPoweredByProvenanceModal,
  uiVersion,
  claimStatus
}) => {
  let evidenceSection

  const orgName =
    subject &&
    (subject.type === 'User' || subject.type === 'Organization' ? subject.name : subject.organizationName)

  const eventLabel = `${orgName} / ${subject && subject.name}`

  if (uiVersion > 1) {
    evidenceSection = null
  } else if (evidence) {
    evidenceSection = withEvidence(evidence, issuer, eventLabel)
  } else if (activities) {
    evidenceSection = withEvidenceFromActivity(activities[0])
  }

  const closeButton = inJourney ? null : <JourneyClose />

  const locale = window.provenance.locale

  return (
    <article
      class={`ClaimCard ClaimCard--evidence
        ${inJourney ? 'ClaimCard--inJourney' : ''}`}
    >
      <ProofPointStickyHeader title={name} type={typeLabel} iconSvg={iconSvg} claimStatus={claimStatus} />
      <div class='ModalCard-content ModalCard-content--flush'>
        {theme && locale === 'en' && (
          uiVersion > 1
            ? <ThemeSection {...theme} analyticsContext={eventLabel} />
            : <ThemeSectionV1 {...theme} analyticsContext={eventLabel} />
        )}

        {uiVersion > 1 && shortDescription &&
          <DescriptionSection
            iconSvg={iconSvg}
            title={shortDescription}
            description={description}
            analyticsContext={eventLabel}
          />}

        {uiVersion > 1 && statement &&
          <QuoteSection
            quote={statement}
            subject={subject}
            initiatives={initiatives}
            issuer={issuer}
            journeys={journeys}
            onStoryClick={onStoryClick}
            name={name}
            analyticsContext={eventLabel}
          />}

        {uiVersion === 1 &&
          <HowAchievedSection
            initiatives={initiatives}
            issuer={issuer}
            journeys={journeys}
            onStoryClick={onStoryClick}
            name={name}
            analyticsContext={eventLabel}
          >
            {statement && <p class='ModalCard-para'>{statement}</p>}
          </HowAchievedSection>}

        {locale === 'en' && <Lookup url={recyclingLookupUrl} proofPointName={scheme && scheme.name} eventLabel={eventLabel} />}

        {verifierSection(name, claimStatus, verifier, activities, eventLabel)}

        {uiVersion === 1 && verifier && <ThirdPartyVerification claimType='Claim' />}

        <ClaimDescription title={name} description={description} url={scheme && scheme.website} />

        {evidenceSection}

        {uiVersion > 1 && evidence &&
          <VerificationSection
            activities={activities}
            analyticsContext={eventLabel}
            description={evidence.description}
            issuer={issuer}
            label={evidence.typeLabel}
            url={(evidence.attachment && evidence.attachment.mediaUrl) || evidence.url}
            claimStatus={claimStatus}
          />}

        {!inJourney && subject && subject.type === 'Product' && !hideAssociatedProductInModal && (
          <ProductSummary {...subject} />
        )}

        {networkExplorerUrl &&
          <BlockchainProof
            url={networkExplorerUrl}
            hash={ipfsHash}
            analyticsContext={eventLabel}
          />}

        {!ipfsHash && <EvidencedMeaning />}

        <Footer
          onAboutClick={onAboutClick}
          showPoweredByProvenanceModal={showPoweredByProvenanceModal}
        >
          <Feedback
            context='Proof Point - Claim'
            organizationName={orgName}
            analyticsContext={eventLabel}
            analyticsName={analyticsName}
          />
        </Footer>
      </div>
      {closeButton}
    </article>
  )
}

EvidenceCard.defaultProps = {
  activities: null,
  evidence: null,
  initiatives: [],
  statement: null,
  inJourney: false,
  ipfsHash: null,
  networkExplorerUrl: null,
  subject: '',
  verifier: null,
  journeys: [],
  theme: null
}

export default EvidenceCard
